// [libx264 @ 0xe00510] Possible presets: ultrafast superfast veryfast faster fast medium slow slower veryslow placebo
import {
  AudioConfig,
  Libx264VideoCompressionPreset,
  type VideoConfig,
} from '../../dorian-shared/types/media/MediaConfigs';

export const NORMALIZED_VIDEO_SIZE = { width: 1080, height: 1920 };

const TARGET_AR = NORMALIZED_VIDEO_SIZE.width / NORMALIZED_VIDEO_SIZE.height; // 0.5625

/**
 * Largest 9:16 box that the source can fill without being upscaled.
 * Returns even dimensions, required by yuv420p.
 */
export const coverTarget = (srcW: number, srcH: number) => {
  // probe failed or degenerate/non-finite source -> fall back to full target (matches old behavior)
  if (!Number.isFinite(srcW) || !Number.isFinite(srcH) || srcW < 2 || srcH < 2) {
    return { ...NORMALIZED_VIDEO_SIZE };
  }
  let w: number;
  let h: number;
  if (srcW / srcH > TARGET_AR) { // wider than 9:16 -> height binds
    h = Math.min(NORMALIZED_VIDEO_SIZE.height, srcH);
    w = Math.round(h * TARGET_AR);
  } else { // taller than 9:16 -> width binds
    w = Math.min(NORMALIZED_VIDEO_SIZE.width, srcW);
    h = Math.round(w / TARGET_AR);
  }
  const evenW = w - (w % 2);
  const evenH = h - (h % 2);
  if (evenW < 2 || evenH < 2) return { ...NORMALIZED_VIDEO_SIZE }; // never emit an invalid 0-dim filter
  return { width: evenW, height: evenH };
};

export const compileFfmpegAudioParams = (videoConfig: AudioConfig): string[] => {
  const audioFrequency = videoConfig?.audio_frequency ?? '44100';
  const audioTargetBitrate = videoConfig?.audio_target_bitrate ?? '128k';

  return [
    '-err_detect', 'explode',
    '-xerror',
    '-c:a', 'mp3',
    '-b:a', audioTargetBitrate,
    '-ar', audioFrequency,
  ];
};

export const compileFfmpegVideoParams = (videoConfig: VideoConfig): string[] => {
  const audioFrequency = videoConfig?.audio_frequency ?? '44100';
  const audioTargetBitrate = videoConfig?.audio_target_bitrate ?? '128k';
  const videoBufferSize = videoConfig?.video_buffer_size;
  const videoMinBitrate = videoConfig?.video_minrate;
  const videoMaxBitrate = videoConfig?.video_maxrate;
  const videoH264Preset = videoConfig?.video_h264_preset ?? Libx264VideoCompressionPreset.veryslow;
  const videoFrameRate = videoConfig?.video_framerate ?? '30';

  return [
    '-err_detect', 'explode',
    '-xerror',
    '-c:v', 'libx264',
    '-profile:v', 'high',
    // Do not change without re-checking the macroblock count: 1080x1920 is
    // ceil(1080/16) x ceil(1920/16) = 68 x 120 = 8,160 macroblocks against Level
    // 4.0's MaxFS of 8,192. Valid, but ~0.4% of headroom, and the Level 4.0 DPB
    // caps reference frames at 4 here (faster presets use fewer).
    // The -r 30 below also keeps this inside Level 4.0's MaxMBPS budget
    // (8,160 x 30 = 244,800 < 245,760), so 60 fps sources must be resampled.
    '-level:v', '4.0',
    '-pix_fmt', 'yuv420p',
    '-colorspace:v', 'bt709',
    '-color_primaries:v', 'bt709',
    '-color_trc:v', 'bt709',
    '-color_range:v', 'tv',
    '-bsf:v', 'h264_metadata=chroma_sample_loc_type=0',
    '-c:a', 'aac',
    // Downmix to stereo. Without this a 5.1 source is encoded as 5.1 AAC at the
    // bitrate below *in total* — around 21 kbps per channel.
    '-ac', '2',
    '-b:a', audioTargetBitrate,
    '-ar', audioFrequency,
    '-brand', 'mp42', // brand compat
    '-preset', `${videoH264Preset}`, // compression preset
    // Explicit rather than relying on libx264's library default of 23 (ffmpeg's
    // own crf option defaults to -1, i.e. unset, so the library default was what
    // actually applied). -maxrate/-bufsize below stay as a ceiling, giving
    // capped-CRF behaviour.
    // This output is a mezzanine that MediaConvert re-encodes into the HLS
    // ladder, so quality matters here and file size does not: 18 keeps the
    // detail the ladder will be built from. Encode speed is bought back with
    // the -preset above (DB-driven, see the video_h264_preset row).
    '-crf', '18',
    '-movflags', '+faststart', // ability to start video earlier (streaming?)
    ...(typeof videoMinBitrate === 'string' ? ['-minrate', videoMinBitrate] : []),
    ...(typeof videoMaxBitrate === 'string' ? ['-maxrate', videoMaxBitrate] : []),
    ...(typeof videoBufferSize === 'string' ? ['-bufsize', videoBufferSize] : []),
    '-r', videoFrameRate,
  ];
};
